import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { UserPlus, Plus, Trash2, Edit, AlertCircle, Loader2, DatabaseZap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertDescription,
  Checkbox,
  DataTable,
  Pagination,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  SearchInput,
  useAuth,
  useLocale,
} from '@aaas/shared';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import type { DataTableColumn, DataTableAction } from '@aaas/shared';
import { getDefaultRecruitmentTypes } from '../../constants/referenceDefaults';
import { useRecruitmentTypeListQuery } from '../../hooks/useRecruitmentTypeQueries';
import recruitmentTypeService from '../../services/recruitment-type.service';
import { RecruitmentTypeFormDialog } from '../../components/RecruitmentTypeFormDialog';
import { useATSRole } from '../../hooks/useATSRole';
import type { RecruitmentType } from '../../types/recruitment-type';

export function RecruitmentTypeListPage() {
  const { user, idToken, currentWorkspace } = useAuth();
  const { isLeaderOrAbove } = useATSRole();
  const { t } = useTranslation('ats');
  const { locale } = useLocale();
  const defaultRecruitmentTypes = useMemo(
    () => getDefaultRecruitmentTypes(locale),
    [locale],
  );
  const { page, size, sortBy, order, filters, setPage, setSize, setSort, setFilters } =
    useUrlPagination({ filterKeys: ['search'] });
  const searchValue = filters.search as string | undefined;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRecruitmentType, setSelectedRecruitmentType] = useState<RecruitmentType | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingRecruitmentType, setEditingRecruitmentType] = useState<RecruitmentType | null>(
    null
  );
  const [showDefaultDialog, setShowDefaultDialog] = useState(false);
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false);
  const [selectedDefaults, setSelectedDefaults] = useState<Set<string>>(
    () => new Set(defaultRecruitmentTypes),
  );

  useEffect(() => {
    setSelectedDefaults(new Set(defaultRecruitmentTypes));
  }, [defaultRecruitmentTypes]);

  // React Query for RecruitmentType list
  const {
    data: recruitmentTypeResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useRecruitmentTypeListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    sortBy,
    order,
    search: searchValue,
    enabled: !!user && !!currentWorkspace,
    refetchInterval: 5000,
  });

  // Extract data from response
  const recruitmentTypes = recruitmentTypeResponse?.data?.items || [];
  const totalItems = recruitmentTypeResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const handleCreateClick = useCallback(() => {
    setFormMode('create');
    setEditingRecruitmentType(null);
    setShowFormDialog(true);
  }, []);

  const handleEditClick = useCallback((record: RecruitmentType) => {
    setFormMode('edit');
    setEditingRecruitmentType(record);
    setShowFormDialog(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setShowFormDialog(false);
    setEditingRecruitmentType(null);
  }, []);

  const handleFormSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleDeleteClick = useCallback((record: RecruitmentType) => {
    setSelectedRecruitmentType(record);
    setDeleteError(null);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!selectedRecruitmentType || !user) return;

    setIsDeleting(true);
    setDeleteError(null);
    setDeletingIds((prev) => new Set(prev).add(selectedRecruitmentType.id));

    try {
      const token = await user.getIdToken();
      await recruitmentTypeService.deleteRecruitmentType(currentWorkspace!.id, selectedRecruitmentType.id, token);
      await refetch();
      setShowDeleteDialog(false);
      setSelectedRecruitmentType(null);
    } catch (err: unknown) {
      let errorMessage = t('config.unknownError');
      if (axios.isAxiosError(err)) {
        errorMessage =
          err.response?.data?.message || err.response?.data?.detail || err.message || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        if (selectedRecruitmentType) {
          newSet.delete(selectedRecruitmentType.id);
        }
        return newSet;
      });
    }
  };

  const handleDialogClose = () => {
    if (!isDeleting) {
      setShowDeleteDialog(false);
      setSelectedRecruitmentType(null);
      setDeleteError(null);
    }
  };

  const handleCreateDefaults = async () => {
    if (!user || !currentWorkspace || selectedDefaults.size === 0) return;
    setIsCreatingDefaults(true);
    try {
      const token = await user.getIdToken();
      const names = defaultRecruitmentTypes.filter((r) => selectedDefaults.has(r));
      await recruitmentTypeService.bulkCreateRecruitmentTypes(currentWorkspace.id, token, names);
      await refetch();
      setShowDefaultDialog(false);
    } catch {
      // surfaced via refetch
    } finally {
      setIsCreatingDefaults(false);
    }
  };

  // Define table columns
  const columns: DataTableColumn<RecruitmentType>[] = [
    {
      key: 'name',
      title: t('recruitmentType.columnName'),
      sortable: true,
      render: (value: string) => <span className="font-medium text-gray-900">{value}</span>,
    },
    {
      key: 'created_at',
      title: t('config.columnCreated'),
      width: 120,
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  // Define table actions (only for leader+)
  const actions: DataTableAction<RecruitmentType>[] = isLeaderOrAbove
    ? [
        {
          key: 'edit',
          label: t('config.edit'),
          icon: <Edit className="h-4 w-4" />,
          onClick: handleEditClick,
          inline: true,
        },
        {
          key: 'delete',
          label: t('config.delete'),
          icon: <Trash2 className="h-4 w-4" />,
          loadingIcon: <Loader2 className="h-4 w-4 animate-spin" />,
          onClick: handleDeleteClick,
          loading: (record) => deletingIds.has(record.id),
          destructive: true,
          inline: true,
        },
      ]
    : [];

  if (isLoading && !recruitmentTypeResponse) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UserPlus className="h-8 w-8 text-blue-600" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{t('recruitmentType.pageTitle')}</h1>
              {isFetching && !isLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-500">{t('recruitmentType.pageDescription')}</p>
          </div>
        </div>
        {isLeaderOrAbove && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSelectedDefaults(new Set(defaultRecruitmentTypes));
                setShowDefaultDialog(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <DatabaseZap className="h-4 w-4" />
              {t('config.createDefaults')}
            </button>
            <button
              onClick={handleCreateClick}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t('recruitmentType.newOne')}
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <SearchInput
        placeholder={t('recruitmentType.searchPlaceholder')}
        onSearch={(value) => setFilters({ ...filters, search: value || undefined })}
        initialValue={searchValue || ''}
      />

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('recruitmentType.loadFailed')}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className="space-y-4">
        <DataTable
          columns={columns}
          data={recruitmentTypes}
          loading={isLoading && !recruitmentTypeResponse}
          actions={actions}
          rowKey={(record) => record.id}
          emptyMessage={t('recruitmentType.empty')}
          onSort={setSort}
          sortKey={sortBy}
          sortDirection={order}
        />

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={size}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={handleDialogClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('recruitmentType.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('recruitmentType.deleteBody')}
            </AlertDialogDescription>
            {deleteError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-600">{deleteError}</span>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleDeleteConfirm();
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.deleting')}
                </>
              ) : (
                t('config.delete')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit RecruitmentType Dialog */}
      <RecruitmentTypeFormDialog
        open={showFormDialog}
        onClose={handleFormClose}
        mode={formMode}
        recruitmentType={editingRecruitmentType}
        onSuccess={handleFormSuccess}
      />

      {/* Create Defaults Dialog */}
      <AlertDialog open={showDefaultDialog} onOpenChange={(open) => !isCreatingDefaults && setShowDefaultDialog(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('recruitmentType.defaultsTitle')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>{t('config.createDefaultsSelect')}</p>
                <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={selectedDefaults.size === defaultRecruitmentTypes.length}
                      onCheckedChange={(checked) => {
                        setSelectedDefaults(checked ? new Set(defaultRecruitmentTypes) : new Set());
                      }}
                    />
                    <span className="text-sm font-medium">{t('config.createDefaultsSelectAll')}</span>
                  </label>
                  <hr className="my-1" />
                  {defaultRecruitmentTypes.map((item) => (
                    <label key={item} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedDefaults.has(item)}
                        onCheckedChange={(checked) => {
                          setSelectedDefaults((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(item);
                            else next.delete(item);
                            return next;
                          });
                        }}
                      />
                      <span className="text-sm">{item}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-3 text-sm">{t('config.createDefaultsSkipNote')}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCreatingDefaults}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleCreateDefaults();
              }}
              disabled={isCreatingDefaults || selectedDefaults.size === 0}
            >
              {isCreatingDefaults ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.creating')}
                </>
              ) : (
                t('config.confirm')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
